import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Helper function for currency formatting
const formatCurrency = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value);
};

// Table component used by ProfitLossTab
const Table = ({ headers, keys, data, formatters, boldRows = [] }) => (<div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"><thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{headers.map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{h}</th>)}</tr></thead><tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">{data.map((row, rowIndex) => (<tr key={rowIndex} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${boldRows.includes(rowIndex) ? 'font-bold' : ''}`}>{keys.map(key => (<td key={key} className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatters[key] ? formatters[key](row[key]) : row[key]}</td>))}</tr>))}</tbody></table></div>);

// Explanation component used by ProfitLossTab
const Explanation = ({ title, children }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
        <div className="mt-8 border-t-2 border-gray-200 dark:border-gray-700 explanation-section">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
                <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">{title}</h4>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-4 animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

// Main ProfitLossTab component
const ProfitLossTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Profit and Loss Statement</h2><Table headers={['Year', 'Operating Income', 'VAT & Surtax', 'Total Profit', 'Income Tax', 'Net Profit', 'Profit Distribution', 'Retained Earnings']} keys={['year', 'operatingIncome', 'vatSurtax', 'totalProfit', 'incomeTax', 'netProfit', 'profitDistribution', 'retainedEarnings']} data={outputs.pnlStatement || []} formatters={{'operatingIncome': formatCurrency, 'vatSurtax': formatCurrency, 'totalProfit': formatCurrency, 'incomeTax': formatCurrency, 'netProfit': formatCurrency, 'profitDistribution': formatCurrency, 'retainedEarnings': formatCurrency}} />
    <Explanation title="How This Works">
        <p>The Profit & Loss (P&L), or Income Statement, shows the project's financial performance over a period of time.</p>
        <p><strong>Principle:</strong> It follows the basic formula: Revenue - Expenses = Profit. It starts with Operating Income (revenue from selling electricity), subtracts all costs (from the Total Cost tab) and taxes, to arrive at the Net Profit (the "bottom line").</p>
        <p><strong>Goal:</strong> To determine if the project is profitable. The P&L also shows how much profit is available to be distributed to investors as dividends and how much is retained in the business to support future growth.</p>
    </Explanation>
</div>);

export default ProfitLossTab; 